import { useState } from 'react';
import { useT } from '@/store';
import { useQuarries } from '@/hooks/useDirectory';
import { QUARRY_CATEGORIES, useDeleteQuarry, useRestoreQuarry, useSaveQuarry } from '@/hooks/useDataAdmin';
import { EmptyState, ErrorState, Panel, SkeletonRows } from '@/components/ui/Panel';
import { Modal } from '@/components/ui/Overlay';
import { ConfirmButton } from '@/components/ui/ConfirmButton';
import { CoordinateField } from '@/components/ui/CoordinateField';
import { PaginationFooter, SearchInput } from '@/components/ui/TableToolbar';
import { useOffsetClamp, usePagedList } from '@/hooks/usePagedList';
import { SmartImportModal } from './SmartImportModal';
import { ArchivedBadge } from '@/components/ui/ArchivedBadge';
import { parseDecimalInput } from '@/lib/format';
import { QuarryPriceList } from './QuarryPriceList';

const EMPTY_FORM = { name: '', lat: '', lng: '', materials_available: '', category: 'own', geofence_radius_m: '' };

/**
 * Quarries, with their price lists nested one level down.
 *
 * `category` is not cosmetic: 'own' quarries are the ones this tenant loads
 * from and prices against, 'competitor' sites are shown on the dispatcher map
 * for context only. The filter defaults to showing both because a dispatcher
 * comparing options needs to see them together.
 *
 * Prices are loaded per quarry, on expand — a fleet with forty quarries
 * should not fetch forty price lists to render a table of names.
 */
export function QuarryList() {
  const t = useT();
  const { search, onSearchChange, limit, setLimit, offset, setOffset } = usePagedList();
  // Off by default: the archive is for recovering a mistake, not something a
  // dispatcher should have to read past every time they open this screen.
  const [showArchived, setShowArchived] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [editing, setEditing] = useState(null); // null | {quarryId?, ...form}
  const [importing, setImporting] = useState(false);
  const [importingPrices, setImportingPrices] = useState(false);

  // Category is filtered by the server, alongside search. It used to be a
  // `.filter()` over the loaded rows, which was right only while the whole
  // list arrived at once: against a page of 25, picking "Konkurentai" hid
  // every competitor from row 26 onward while the footer still counted all
  // of them. A partial answer that looks complete.
  const { data: visible = [], totalCount, isLoading, isError, error } = useQuarries({
    search,
    category: categoryFilter || undefined,
    limit,
    offset,
    includeInactive: showArchived,
  });
  // V-296. Deleting the last quarry on a later page shrinks totalCount
  // without ever touching offset — without this, the list reads a false
  // "no quarries yet" over a set that still has rows one page back.
  useOffsetClamp(totalCount, limit, offset, setOffset);
  const saveQuarry = useSaveQuarry();
  const [formError, setFormError] = useState(null);
  const deleteQuarry = useDeleteQuarry();
  const restoreQuarry = useRestoreQuarry();

  const submit = () => {
    // V-351. The decimal comma, on the two boxes where it costs the most.
    // CoordinateField's lat/lng are inputMode="decimal" text inputs, so an
    // lt-locale keyboard offers a COMMA — and Number.parseFloat('54,687')
    // is 54, which is not NaN, so the errCoordinates guard below passes and
    // the quarry is saved seventy-six kilometres from where it is. Nothing
    // in the response says so. core/import_engine.py has folded exactly
    // this comma for the SPREADSHEET road into the same column since it
    // shipped ("Get this wrong on a quarry and it lands at 0,0 — in the
    // Gulf of Guinea"); the form road never did.
    const lat = parseDecimalInput(editing.lat);
    const lng = parseDecimalInput(editing.lng);
    // V-47. This used to be a bare `return`: the first form of the first
    // checklist step, on installation day, and pressing Išsaugoti produced
    // no request, no message and no closed dialog. A carrier cannot tell
    // that from a broken button, and this is the first thing he touches.
    if (!editing.name.trim()) return setFormError('data.errQuarryName');
    if (Number.isNaN(lat) || Number.isNaN(lng)) return setFormError('data.errCoordinates');
    setFormError(null);

    saveQuarry.mutate(
      {
        quarryId: editing.quarryId,
        name: editing.name.trim(),
        lat,
        lng,
        category: editing.category,
        // The API takes a list; the form takes the comma-separated string
        // dispatchers actually type.
        materials_available: editing.materials_available
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        ...(editing.geofence_radius_m ? { geofence_radius_m: Number(editing.geofence_radius_m) } : null),
      },
      { onSuccess: () => setEditing(null) },
    );
  };

  return (
    <Panel
      title={t('data.quarriesHeading')}
      actions={
        <>
          <SearchInput
            value={search}
            onDebouncedChange={onSearchChange}
            placeholder={t('data.quarrySearchPh')}
          />
          <select
            className="field !w-auto !py-1 !text-[11px]"
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">{t('data.allCategories')}</option>
            {QUARRY_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t(`data.category.${c}`)}
              </option>
            ))}
          </select>
          <button type="button" className="btn" onClick={() => setImporting(true)}>
            {t('import.openBtn')}
          </button>
          {/* P1-1: prices live here rather than inside a quarry's own edit
              modal, because a supplier's price list is one file covering
              every quarry and material at once — which is also why the
              import resolves the quarry by the NAME the list is written
              with. Every revenue and margin figure in Analytics is computed
              off this table; a pair with no row here contributes nothing. */}
          <button type="button" className="btn" onClick={() => setImportingPrices(true)}>
            {t('import.titlePrices')}
          </button>
          <label className="flex items-center gap-1.5 text-[11px] text-dim">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => {
                setShowArchived(e.target.checked);
                // Same reason the category filter above resets it: this
                // changes how many rows there are, and asking the server for
                // rows past the end of a shorter filtered list returns an
                // empty page for a filter that plainly matches something.
                setOffset(0);
              }}
            />
            {t('common.showArchived')}
          </label>
          <button type="button" className="btn btn-primary" onClick={() => setEditing({ ...EMPTY_FORM })}>
            {t('common.addBtn')}
          </button>
        </>
      }
    >
      {isError ? <ErrorState error={error} /> : null}
      {isLoading ? (
        <SkeletonRows rows={4} />
      ) : visible.length === 0 ? (
        <EmptyState>{search ? t('common.noMatches') : t('data.noQuarriesYet')}</EmptyState>
      ) : (
        <ul className="space-y-2">
          {visible.map((quarry) => (
            <li
              key={quarry.quarry_id}
              className={`rounded-[11px] border border-line bg-white/2 ${
                quarry.active ? '' : 'opacity-60'
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2 p-3">
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => setExpandedId(expandedId === quarry.quarry_id ? null : quarry.quarry_id)}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold">
                      {quarry.name}
                      {quarry.active ? null : <ArchivedBadge deletedAt={quarry.deleted_at} />}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[9.5px] font-bold uppercase ${
                        quarry.category === 'competitor' ? 'bg-white/8 text-dim' : 'bg-accent/15 text-accent'
                      }`}
                    >
                      {t(`data.category.${quarry.category}`)}
                    </span>
                  </div>
                  <div className="truncate text-[11px] text-dim">
                    {quarry.materials_available || '—'} · {quarry.lat?.toFixed(4)}, {quarry.lng?.toFixed(4)}
                  </div>
                </button>

                <div className="flex gap-1.5">
                  <button
                    type="button"
                    className="btn !px-2.5 !py-1 !text-[11px]"
                    onClick={() =>
                      setEditing({
                        quarryId: quarry.quarry_id,
                        name: quarry.name ?? '',
                        lat: String(quarry.lat ?? ''),
                        lng: String(quarry.lng ?? ''),
                        materials_available: quarry.materials_available ?? '',
                        category: quarry.category ?? 'own',
                        geofence_radius_m: String(quarry.geofence_radius_m ?? ''),
                      })
                    }
                  >
                    {t('common.editBtn')}
                  </button>
                  {quarry.active ? (
                    <ConfirmButton
                      label={t('common.deleteBtn')}
                      confirmLabel={t('common.confirmDelete')}
                      hint={t('data.deleteQuarryHint')}
                      onConfirm={() => deleteQuarry.mutate(quarry.quarry_id)}
                    />
                  ) : (
                    <button
                      type="button"
                      className="btn !px-2.5 !py-1 !text-[11px]"
                      disabled={restoreQuarry.isPending}
                      onClick={() => restoreQuarry.mutate(quarry.quarry_id)}
                    >
                      {t('common.restoreBtn')}
                    </button>
                  )}
                </div>
              </div>

              {expandedId === quarry.quarry_id ? (
                <div className="border-t border-line p-3">
                  <QuarryPriceList quarryId={quarry.quarry_id} />
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {deleteQuarry.isError ? <ErrorState error={deleteQuarry.error} /> : null}
      {restoreQuarry.isError ? <ErrorState error={restoreQuarry.error} /> : null}

      <PaginationFooter
        total={totalCount}
        limit={limit}
        offset={offset}
        onOffsetChange={setOffset}
        onLimitChange={setLimit}
      />

      <SmartImportModal
        open={importing}
        onClose={() => setImporting(false)}
        entityType="quarry"
        title={t('import.titleQuarries')}
      />

      <SmartImportModal
        open={importingPrices}
        onClose={() => setImportingPrices(false)}
        entityType="quarry_price"
        title={t('import.titlePrices')}
      />

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={t(editing?.quarryId ? 'common.editBtn' : 'data.addQuarry')}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setEditing(null)}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn-primary" onClick={submit} disabled={saveQuarry.isPending}>
              {t('common.saveBtn')}
            </button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-3">
            <Field label={t('data.quarryNameLabel')}>
              <input
                className="field"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              />
            </Field>
            {/* V-47a: the two boxes plus the map that has been in this repo
                since the client portal shipped. Untranslated "Lat"/"Lng"
                labels went with them. */}
            <CoordinateField
              lat={editing.lat}
              lng={editing.lng}
              onChange={({ lat, lng }) => setEditing({ ...editing, lat, lng })}
              radiusM={Number.parseFloat(editing.geofence_radius_m) || 200}
            />
            <Field label={t('data.materialsLabel')}>
              <input
                className="field"
                value={editing.materials_available}
                onChange={(e) => setEditing({ ...editing, materials_available: e.target.value })}
                placeholder={t('data.materialsPh')}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('data.categoryLabel')}>
                <select
                  className="field"
                  value={editing.category}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                >
                  {QUARRY_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {t(`data.category.${c}`)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t('data.geofenceLabel')}>
                <input
                  className="field"
                  value={editing.geofence_radius_m}
                  onChange={(e) => setEditing({ ...editing, geofence_radius_m: e.target.value })}
                  inputMode="numeric"
                  placeholder="200"
                />
              </Field>
            </div>
            {formError ? <ErrorState error={new Error(t(formError))} /> : null}
            {saveQuarry.isError ? <ErrorState error={saveQuarry.error} /> : null}
          </div>
        ) : null}
      </Modal>
    </Panel>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[10.5px] font-bold uppercase tracking-[0.03em] text-dim">{label}</span>
      {children}
    </label>
  );
}
